import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router'
import { useEffect, type ReactNode } from 'react'
import { useOrganization } from '../api/queries'
import { useMyOrganizations } from '../api/multiOrg'
import { useMemberKind, usePermissions, holdsPermission } from '../contexts/PermissionsContext'
import { sectionAllowedForKind } from '../lib/sidebar-menus'
import { isPlatformAdminSection, requiredPermissionForPath } from '../lib/route-permissions'
import { useIsPlatformAdmin } from '../hooks/useAuth'
import { notifyPermissionDenied } from '../lib/notifications'
import { requireSession } from '../lib/auth-guard'
import { LoginRedirectedNotice } from '../components/auth/LoginRedirectedNotice'

export const Route = createFileRoute('/$org')({
    component: OrganizationLayout,
    beforeLoad: async ({ location, params }) => {
        // Public, org-scoped entry points: the login page and the
        // password-reset flow (spec 2026-08-01-16). A signed-out user must be
        // able to reach both — redirecting the reset link to /login would make
        // the emailed token unusable.
        const publicPaths = [
            `/${params.org}/login`,
            `/${params.org}/password-reset`,
            // The sign-in link mailed to residents and providers (spec
            // 2026-10-06-06): the token is the credential.
            `/${params.org}/sign-in-link`,
        ]
        if (publicPaths.includes(location.pathname)) {
            return
        }

        // Signed out, or restricted to MFA enrollment (spec 2026-10-01-03):
        // shared with the org-less /organizations layout.
        requireSession(params.org, location.href)
    },
})

function OrganizationLayout() {
    const { org } = Route.useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const { error: orgError, isLoading: orgLoading } = useOrganization(org)
    // Pull the caller's reachable orgs + server-picked default from the new
    // multi-org endpoint. Falls back to the org in the URL if the endpoint
    // is not available yet (e.g. during the transition).
    const { data: myOrgs } = useMyOrganizations()

    const isOrgNotFound = orgError && (() => {
        try {
            const parsed = JSON.parse(orgError.message)
            return parsed.code === 'organization_not_found'
        } catch {
            return false
        }
    })()

    useEffect(() => {
        if (!isOrgNotFound) return

        // The backend tells us which org to use via `default_org_code`.
        // When it's empty (or the user isn't a member of any org) we send
        // them to the no-organization fallback so they can accept an
        // invitation or create their own. We only fall back to 'demo' if
        // the endpoint hasn't loaded yet — the effect re-runs on every
        // myOrgs update so the final redirect will use the real value.
        const defaultOrg = myOrgs?.default_org_code

        if (myOrgs && !defaultOrg && (myOrgs.data?.length ?? 0) === 0) {
            navigate({ to: '/no-organization', replace: true })
            return
        }

        const target = defaultOrg || myOrgs?.data?.[0]?.code || 'demo'
        if (target !== org) {
            const subPath = location.pathname.replace(`/${org}`, `/${target}`)
            navigate({
                to: subPath,
                replace: true,
            })
        }
    }, [isOrgNotFound, myOrgs, org, navigate, location.pathname])

    // Don't render children while loading or if org not found (redirect pending)
    if (orgLoading || isOrgNotFound) {
        return null
    }

    // LiveEventsProvider is mounted higher up, in routes/__root.tsx's
    // LiveEventsGate (components/LiveEventsGate.tsx) — the header's live-status
    // indicator lives ABOVE this outlet in the tree (in AppShell, outside any
    // per-route layout), so a provider scoped to only routed content could
    // never reach it via React context. See LiveEventsContext.tsx and __root.tsx's LiveEventsGate.
    //
    // PermissionsProvider is mounted in the very same place and for the very
    // same reason (routes/__root.tsx's PermissionsGate): AppSidebar hides its
    // entries by permission and lives ABOVE this outlet. The guard below
    // consumes that provider from here.
    return (
        <SectionPermissionGuard org={org}>
            <LoginRedirectedNotice org={org} />
            <Outlet />
        </SectionPermissionGuard>
    )
}

// SectionPermissionGuard is the route guard for the whole `$org` tree.
//
// TanStack Router's `beforeLoad` runs outside React and has no access to the
// membership query that carries the permission set, so the guard is expressed
// where the data actually lives: one component in the org layout, driven by the
// single path→permission table in lib/route-permissions.ts. Every `$org/*`
// route passes through here, so a new section is gated the moment it is added
// to that table — there is no per-route wiring to forget.
//
// It never *flickers* closed: while the membership query is in flight nothing is
// rendered and nothing is redirected. And it only ejects on a KNOWN answer — a
// membership summary that was actually found and does not carry the section's
// read permission. A failed or unauthenticated `/users/me/organizations` leaves
// the page in place and lets the API answer: hiding a control we cannot justify
// is cheap, throwing someone out of a page the backend would have served is not.
function SectionPermissionGuard({
    org,
    children,
}: {
    org: string
    children: ReactNode
}) {
    const location = useLocation()
    const navigate = useNavigate()
    const { permissions, isLoading, membershipKnown } = usePermissions()
    const kind = useMemberKind()
    const isPlatformAdmin = useIsPlatformAdmin()

    const required = requiredPermissionForPath(location.pathname, org)
    // holdsPermission, not includes: a resident's set is `issues.read_shared` /
    // `issues.read_own`, never `issues.read`, and the backend's `satisfies`
    // treats the narrowed codes as answering the base check. A plain includes()
    // bounces them off their own issue page while the API serves it.
    const allowed = !required || holdsPermission(permissions, required)
        || (isPlatformAdmin && isPlatformAdminSection(location.pathname, org))
    // The second line, after the permission: a member kind with its own
    // shell (a resident) opens only the sections of that shell, the very
    // table its sidebar is drawn from (lib/sidebar-menus.ts, spec
    // 2026-10-07-03). A resident holds `organization.read`, yet the activity
    // log and the settings are not theirs. The kind is only known once the
    // membership is, so this never ejects on an unknown answer either.
    const kindDenied = membershipKnown && !sectionAllowedForKind(kind, location.pathname, org)
    const denied = (Boolean(required) && membershipKnown && !allowed) || kindDenied

    useEffect(() => {
        if (!denied || isLoading) return
        notifyPermissionDenied(required)
        navigate({ to: '/$org', params: { org }, replace: true })
    }, [isLoading, denied, required, navigate, org])

    // An ungated section (home, search, the user's own profile, and the public
    // login / password-reset entry points) renders immediately — waiting on the
    // membership query there would blank the very pages a signed-out user needs.
    if (!required && !kindDenied) {
        return <>{children}</>
    }
    if (isLoading || denied) {
        return null
    }
    return <>{children}</>
}
